import { useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { EventOccurrence } from "@shared/types";
import { AuthProvider, useAuth } from "@/hooks/AuthContext";
import { ReferenceDataProvider, useReferenceData } from "@/hooks/ReferenceDataContext";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useSearch } from "@/hooks/useSearch";
import { TopNav } from "@/components/TopNav";
import { FiltersBar } from "@/components/FiltersBar";
import { DashboardSummary } from "@/components/DashboardSummary";
import { CalendarView } from "@/components/CalendarView";
import type { CalendarViewHandle, CalendarViewName } from "@/components/CalendarView";
import { SearchResultsList } from "@/components/SearchResultsList";
import { EventDetailDrawer } from "@/components/EventDetailDrawer";
import { EventFormModal } from "@/components/EventFormModal";
import type { EventFormInitial } from "@/components/EventFormModal";
import { DeleteEventDialog } from "@/components/DeleteEventDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { SettingsPanel } from "@/components/SettingsPanel";
import { LoginModal } from "@/components/LoginModal";
import { EmptyState } from "@/components/EmptyState";
import { eventsApi } from "@/api/client";
import { startOfDayIso } from "@/utils/date";

function CalendarApp() {
  const { isAdmin, logout } = useAuth();
  const { people, eventTypes, sources, loading: refLoading, refresh } = useReferenceData();
  const activePeople = useMemo(() => people.filter((p) => !p.archived), [people]);
  const activeTypes = useMemo(() => eventTypes.filter((t) => !t.archived), [eventTypes]);

  const calendarRef = useRef<CalendarViewHandle>(null);
  // Default to a scrollable day-grouped agenda on phones (the calendar grid is cramped at
  // that width); desktop/iPad keep the traditional month grid. Only the initial choice is
  // automatic — once loaded, switching views is entirely up to the person using the app.
  const [view, setView] = useState<CalendarViewName>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches ? "listMonth" : "dayGridMonth",
  );
  const [range, setRange] = useState<{ start: string; end: string; title: string } | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [formState, setFormState] = useState<EventFormInitial | null>(null);
  const [detailOccurrence, setDetailOccurrence] = useState<EventOccurrence | null>(null);
  const [deleteOccurrence, setDeleteOccurrence] = useState<EventOccurrence | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const mainFilter = useMemo(
    () => (range ? { start: range.start, end: range.end, personIds: selectedPeople, eventTypeIds: selectedTypes } : null),
    [range, selectedPeople, selectedTypes],
  );
  const { events, refetch: refetchMain } = useCalendarEvents(mainFilter);

  const dashboardFilter = useMemo(() => {
    const start = startOfDayIso(new Date());
    const end = new Date(new Date(start).getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
    return { start, end, personIds: selectedPeople, eventTypeIds: selectedTypes };
  }, [selectedPeople, selectedTypes]);
  const { events: dashboardEvents, refetch: refetchDashboard } = useCalendarEvents(dashboardFilter);

  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const { results: searchResults, loading: searchLoading } = useSearch(searchQuery, searchRefreshKey);

  // Any create/edit/delete/import can affect the calendar grid, the dashboard summary, and
  // whatever search results happen to be showing — refresh all three together.
  function refetch() {
    refetchMain();
    refetchDashboard();
    setSearchRefreshKey((k) => k + 1);
  }

  function togglePerson(id: string) {
    setSelectedPeople((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }
  function toggleType(id: string) {
    setSelectedTypes((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }

  function requireAdminOr(action: () => void) {
    if (isAdmin) action();
    else setLoginOpen(true);
  }

  function openCreate(startIso: string, allDay: boolean) {
    requireAdminOr(() => setFormState({ kind: "create", startIso, allDay }));
  }
  function openEdit(occurrence: EventOccurrence) {
    setDetailOccurrence(null);
    setFormState({ kind: "edit", occurrence });
  }
  async function handleDuplicate(occurrence: EventOccurrence) {
    await eventsApi.duplicate(occurrence.id);
    setDetailOccurrence(null);
    refetch();
  }
  async function handleDelete(scope: "occurrence" | "series") {
    if (!deleteOccurrence) return;
    await eventsApi.remove(deleteOccurrence.id, scope, deleteOccurrence.occurrenceStart);
    setDeleteOccurrence(null);
    setDetailOccurrence(null);
    refetch();
  }

  const showSearch = searchQuery.trim().length > 0;

  return (
    <div
      className="flex min-h-screen flex-col bg-canvas pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-ink dark:bg-canvas-dark dark:text-ink-dark"
    >
      <TopNav
        title={range?.title ?? ""}
        view={view}
        onChangeView={(v) => {
          setView(v);
          calendarRef.current?.changeView(v);
        }}
        onToday={() => calendarRef.current?.today()}
        onPrev={() => calendarRef.current?.prev()}
        onNext={() => calendarRef.current?.next()}
        onSearch={setSearchQuery}
        onAddEvent={() => requireAdminOr(() => setFormState({ kind: "create", startIso: new Date().toISOString(), allDay: false }))}
        onImport={() => requireAdminOr(() => setImportOpen(true))}
        onOpenSettings={() => requireAdminOr(() => setSettingsOpen(true))}
        isAdmin={isAdmin}
        onSignIn={() => setLoginOpen(true)}
        onSignOut={() => void logout()}
      />

      <main className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col gap-4 px-4 py-5 sm:px-6">
        {!refLoading && view !== "listMonth" && !showSearch && (
          <DashboardSummary
            events={dashboardEvents}
            people={activePeople}
            eventTypes={activeTypes}
            onSelectOccurrence={setDetailOccurrence}
          />
        )}

        <div className="hidden rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-4 py-3 shadow-card sm:block">
          <FiltersBar
            people={activePeople}
            eventTypes={activeTypes}
            selectedPeople={selectedPeople}
            selectedTypes={selectedTypes}
            onTogglePerson={togglePerson}
            onToggleType={toggleType}
            onClear={() => {
              setSelectedPeople([]);
              setSelectedTypes([]);
            }}
          />
        </div>

        {showSearch ? (
          <SearchResultsList
            query={searchQuery}
            results={searchResults}
            loading={searchLoading}
            people={activePeople}
            eventTypes={activeTypes}
            onSelectOccurrence={setDetailOccurrence}
          />
        ) : (
          <div
            className={clsx(
              "flex min-h-[600px] flex-1 flex-col rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark shadow-card",
              view === "listMonth" ? "overflow-visible" : "overflow-hidden",
            )}
          >
            {activeTypes.length === 0 ? (
              <EmptyState title="Setting things up" description="Loading your calendar…" />
            ) : (
              <CalendarView
                ref={calendarRef}
                events={events}
                people={activePeople}
                eventTypes={activeTypes}
                initialView={view}
                onRangeChange={setRange}
                onSelectOccurrence={setDetailOccurrence}
                onCreateAt={openCreate}
                canCreate={isAdmin}
              />
            )}
          </div>
        )}
      </main>

      <EventDetailDrawer
        occurrence={detailOccurrence}
        people={people}
        eventTypes={eventTypes}
        isAdmin={isAdmin}
        onClose={() => setDetailOccurrence(null)}
        onEdit={openEdit}
        onDuplicate={handleDuplicate}
        onDelete={setDeleteOccurrence}
      />

      <EventFormModal
        open={Boolean(formState)}
        initial={formState}
        people={activePeople}
        eventTypes={activeTypes}
        onClose={() => setFormState(null)}
        onSaved={refetch}
      />

      <DeleteEventDialog occurrence={deleteOccurrence} onCancel={() => setDeleteOccurrence(null)} onConfirm={handleDelete} />

      <ImportDialog
        open={importOpen}
        people={activePeople}
        eventTypes={activeTypes}
        sources={sources}
        onClose={() => setImportOpen(false)}
        onImported={refetch}
      />

      <SettingsPanel
        open={settingsOpen}
        people={people}
        eventTypes={eventTypes}
        onClose={() => setSettingsOpen(false)}
        onChanged={refresh}
      />

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ReferenceDataProvider>
        <CalendarApp />
      </ReferenceDataProvider>
    </AuthProvider>
  );
}
